<script>
import { authMethods } from '@state/helpers'
import axios from 'axios'

export default {
  page: {
    title: 'Signin',
    meta: [{ name: 'description', content: 'The safehaven Signin page.' }],
  },
  data() {
    return {
      show1: false,
      email: '',
      emailRules: [
        (v) => !!v || 'Email address is required to sign in',
        (v) =>
          /^(([^<>()[\]\\.,;:\s@']+(\.[^<>()\\[\]\\.,;:\s@']+)*)|('.+'))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
            v
          ) || 'Email address is required to sign in',
        (v) => {
          this.emailAuthError = ''
          return true
        },
      ],
      password: '',
      passwordRules: [
        (v) => !!v || 'Password is required to sign in',
        (v) => {
          this.passwordAuthError = ''
          return true
        },
      ],
      message: '',
      emailAuthError: '',
      passwordAuthError: '',
      commonErr: null,
      authError: null,
      tryingToLogIn: false,
      valid: false,
      isActive: false,
      hasError: false,
    }
  },
  mounted() {
    this.valid = false
  },

  methods: {
    ...authMethods,
    handleSubmit() {
      if (!this.emailAuthError && !this.passwordAuthError) {
        this.$refs.form.validate()

        if (this.valid) {
          this.tryToLogIn()
        }
      }
    },

    tryToLogIn() {
      if (
        this.email === null ||
        this.email === '' ||
        this.password === null ||
        this.password === '' ||
        this.passwordAuthError
      ) {
        this.isActive = true
        this.hasError = true
      } else {
        this.tryingToLogIn = true
        // Reset the authError if it existed.
        const orgDetails = JSON.parse(
          localStorage.getItem('organisationDetails')
        )
        return this.logIn({
          email: this.email,
          password: this.password,
          org: orgDetails.org,
        })
          .then((token) => {
            this.tryingToLogIn = false
            if (token && token.user) {
              let hasEmailMfa = false
              for (let i = 0; i < token.user.mfa_devices.length; i++) {
                if (token.user.mfa_devices[i].device_type === 'email') {
                  hasEmailMfa = true
                }
              }
              if (hasEmailMfa) {
                axios
                  .post('/api/v1/mfa/generate/')
                  .then(() =>
                    this.$router.push({
                      name: 'verify',
                      params: { routeTo: this.$route.query.redirectFrom },
                    })
                  )
                  .catch((error) => {
                    this.tryingToLogIn = false
                    this.authError = error
                  })
              }
              if (
                this.$store.state.calender.recordDetails?.path ===
                'safehaven-health-record'
              ) {
                this.$router.push({
                  name: 'safehaven-health-record',
                  params: { showReceivedRecords: true },
                })
              } else if (
                this.$store.state.calender.recordDetails?.path ===
                'health-vault'
              ) {
                this.$router.push({ name: 'safehaven-health-vault' })
              } else {
                this.$router.push({ name: 'safehaven-dashboard' })
              }
            }
            if (token.response.status === 401) {
              const errorMsg = token.response.data.toLowerCase()
              if (errorMsg.includes('password')) {
                this.passwordAuthError = token.response.data
              } else if (errorMsg.includes('User')) {
                this.emailAuthError = token.response.data
              } else {
                this.commonErr = token.response.data
              }
            }
          })
          .catch((error) => {
            if (error.response) {
              this.tryingToLogIn = false
              const errorMsg = error.response.data.toLowerCase()
              if (errorMsg.includes('password')) {
                this.passwordAuthError = error.response.data
              } else if (errorMsg.includes('User')) {
                this.emailAuthError = error.response.data
              } else {
                this.commonErr = error.response.data
              }
            }
          })
      }
    },
  },
}
</script>

<template>
  <v-container fluid>
    <v-row no-gutters>
      <v-col align="center" class="mb-4" :class="$style.title">
        Sign in to your account
      </v-col>
    </v-row>
    <v-container :class="$style.innerContainer">
      <v-row no-gutters justify="center">
        <v-form
          ref="form"
          v-model="valid"
          lazy-validation
          @submit.prevent="handleSubmit"
        >
          <v-row :class="$style.text" class="mb-2" no-gutters justify="center">
            <v-col
              v-if="commonErr"
              cols="12"
              class="mb-4"
              align="center"
              :class="$style.serverErrorMsg"
            >
              {{ commonErr }}
            </v-col>
            <v-col cols="12" md="12">
              <span :class="$style.textLabel">Email</span
              ><span :class="$style.mandatory">*</span>
            </v-col>
            <v-col cols="12" md="12" class="mt-2">
              <v-text-field
                v-model="email"
                :rules="emailRules"
                :error-messages="emailAuthError"
                hide-details="auto"
                type="email"
                dense
                outlined
                required
                :class="{
                  'error--text': isActive,
                  'text-danger': hasError,
                  'font-size': '35px',
                }"
                @input="commonErr = null"
              ></v-text-field>
              <p v-if="authError" :class="$style.serverErrorMsg">
                {{ authError.response.data }}
              </p>
            </v-col>
          </v-row>

          <v-row :class="$style.text" class="mb-2" no-gutters justify="center">
            <v-col cols="12" md="12">
              <span :class="$style.textLabel">Password</span>
              <span :class="$style.mandatory">*</span>
            </v-col>
            <v-col cols="12" md="12" class="mt-2">
              <v-text-field
                v-model="password"
                :rules="passwordRules"
                :error-messages="passwordAuthError"
                hide-details="auto"
                dense
                :class="{ 'error--text': isActive, 'text-danger': hasError }"
                outlined
                required
                :type="show1 ? 'text' : 'password'"
                :append-icon="show1 ? 'mdi-eye' : 'mdi-eye-off'"
                @click:append="show1 = !show1"
                @input="commonErr = null"
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row justify="center" no-gutters>
            <v-col cols="12" md="12" class="mt-4" align="center">
              <v-btn
                type="submit"
                class="my-auto text-capitalize primary-btn w-m-90-btn"
              >
                Sign In
              </v-btn>
            </v-col>
          </v-row>

          <v-row
            class="px-3"
            :class="$style.forgot"
            no-gutters
            justify="center"
          >
            <v-col cols="12" md="8" class="mt-2" align="center">
              <router-link
                class="text-decoration-none"
                :to="{ name: 'safehaven-forgotpassword' }"
                >Forgot password?
              </router-link>
            </v-col>
          </v-row>
        </v-form>
      </v-row>
      <v-row
        class="px-3"
        :class="$style.newAccount"
        no-gutters
        justify="center"
      >
        <v-col cols="12" md="8" class="mt-2" align="center">
          <span :class="$style.bottomText">
            New to {{ $route.params.clientId }}? &nbsp;<router-link
              class="text-decoration-none"
              :to="{
                name: 'safehaven-access-code',
                params: { clientId: $route.params.clientId },
              }"
              >Enter your access code
            </router-link>
          </span>
        </v-col>
      </v-row>
    </v-container>
  </v-container>
</template>

<style lang="scss" module>
@import '@design';

.textLabel {
  font-size: 12px;
  font-weight: 700;
}
.mandatory {
  color: #da1414;
}
.title {
  font-size: 1.25em;
}
.text,
.representativeText {
  margin: 0.5rem auto;
  color: #2c3a4b;
}
.representativeText {
  font-size: 12px !important;
}
.textField {
  font-size: 15px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.75px;
}
.signUpText {
  font-size: 12px;
  color: #858c94;
}
.inlineLink {
  display: inline;
}

.forgot {
  margin-top: 25px !important;
  font-size: 14px;
  font-weight: 600;
  line-height: 24px;
  color: rgb(70, 70, 172);
  letter-spacing: 0.75px;
}

.serverErrorMsg {
  margin-top: 2px;
  margin-left: 10px;
  font-size: 13px;
  color: #ca024f;
}

.newAccount {
  font-size: 10px;
  font-weight: 600;
  line-height: 24px;
  color: rgb(176, 176, 182);
  letter-spacing: 0.75px;
}
.bottomText {
  font-size: 12px;
}
</style>

<style scoped>
.v-text-field >>> input {
  font-size: 14px !important;
}
</style>
